import { Tags } from "../enum/tags";
import { apiSlice } from "./http";
import { toQueryParams } from "@/utils/to-query-params";
import {
  IAggregationsParams,
  IAggregationsResponse,
  ICommitAllBody,
  ICommitAllResponse,
  ICreateDraftBody,
  IDepositBankOption,
  IDepositBatchesParams,
  IDepositBatchesResponse,
  IDepositDraftDetail,
  IDepositDraftSummary,
  IDepositItemsParams,
  IDepositItemsResponse,
  IDepositStoresSummaryParams,
  IDepositStoresSummaryResponse,
  IListDraftsParams,
  IReprocessBody,
  IReprocessResponse,
  IUndoBatchBody,
  IUndoBatchResponse,
} from "src/@types/deposit-reconciliation";

export const depositReconciliationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDepositStoresSummary: builder.query<
      IDepositStoresSummaryResponse,
      IDepositStoresSummaryParams
    >({
      query: (filters) => ({
        url: `/deposit-reconciliation/stores-summary?${toQueryParams(filters)}`,
        method: "GET",
      }),
      providesTags: [Tags.DEPOSIT_STORES_SUMMARY],
    }),

    getDepositBanks: builder.query<IDepositBankOption[], void>({
      query: () => ({
        url: "/deposit-reconciliation/banks",
        method: "GET",
      }),
    }),

    getDepositItems: builder.query<IDepositItemsResponse, IDepositItemsParams>({
      query: (filters) => ({
        url: `/deposit-reconciliation/items?${toQueryParams(filters)}`,
        method: "GET",
      }),
      providesTags: [Tags.DEPOSIT_ITEMS],
    }),

    getDepositBatches: builder.query<
      IDepositBatchesResponse,
      IDepositBatchesParams
    >({
      query: (filters) => ({
        url: `/deposit-reconciliation/batches?${toQueryParams(filters)}`,
        method: "GET",
      }),
      providesTags: [Tags.DEPOSIT_BATCHES],
    }),

    listDepositDrafts: builder.query<IDepositDraftSummary[], IListDraftsParams>({
      query: (filters) => ({
        url: `/deposit-reconciliation/drafts?${toQueryParams(filters)}`,
        method: "GET",
      }),
      providesTags: [Tags.DEPOSIT_DRAFTS],
    }),

    getDepositDraftDetail: builder.query<IDepositDraftDetail, number>({
      query: (draftId) => ({
        url: `/deposit-reconciliation/drafts/${draftId}`,
        method: "GET",
      }),
      providesTags: [Tags.DEPOSIT_DRAFTS],
    }),

    createDepositDraft: builder.mutation<{ draftId: number }, ICreateDraftBody>({
      query: (body) => ({
        url: `/deposit-reconciliation/drafts`,
        method: "POST",
        body,
      }),
      invalidatesTags: [Tags.DEPOSIT_DRAFTS, Tags.DEPOSIT_ITEMS],
    }),

    discardDepositDraft: builder.mutation<
      { draftId: number; status: string },
      number
    >({
      query: (draftId) => ({
        url: `/deposit-reconciliation/drafts/${draftId}/discard`,
        method: "POST",
      }),
      invalidatesTags: [Tags.DEPOSIT_DRAFTS, Tags.DEPOSIT_ITEMS],
    }),

    commitAllDepositDrafts: builder.mutation<ICommitAllResponse, ICommitAllBody>({
      query: (body) => ({
        url: `/deposit-reconciliation/drafts/commit-all`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        Tags.DEPOSIT_DRAFTS,
        Tags.DEPOSIT_ITEMS,
        Tags.DEPOSIT_BATCHES,
        Tags.DEPOSIT_STORES_SUMMARY,
      ],
    }),

    undoDepositBatch: builder.mutation<IUndoBatchResponse, IUndoBatchBody>({
      query: ({ batchId, reason }) => ({
        url: `/deposit-reconciliation/batches/${batchId}/undo`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: [
        Tags.DEPOSIT_DRAFTS,
        Tags.DEPOSIT_BATCHES,
        Tags.DEPOSIT_ITEMS,
        Tags.DEPOSIT_STORES_SUMMARY,
      ],
    }),

    reprocessDeposits: builder.mutation<IReprocessResponse, IReprocessBody>({
      query: (body) => ({
        url: `/deposit-reconciliation/reprocess`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        Tags.DEPOSIT_STORES_SUMMARY,
        Tags.DEPOSIT_ITEMS,
        Tags.ASYNC_JOB,
        Tags.JOB_PROGRESS,
      ],
    }),

    getDepositAggregations: builder.query<IAggregationsResponse, IAggregationsParams>({
      query: (params) => ({
        url: `/deposit-reconciliation/aggregations?${toQueryParams(params)}`,
        method: "GET",
      }),
      providesTags: [Tags.DEPOSIT_BATCHES, Tags.DEPOSIT_STORES_SUMMARY],
    }),

    exportDepositExcel: builder.mutation<Blob, IDepositStoresSummaryParams>({
      query: (filters) => ({
        url: `/deposit-reconciliation/export?${toQueryParams(filters)}`,
        method: "GET",
        responseHandler: async (response: Response) => {
          if (response.headers.get("content-type")?.includes("json")) {
            return response.json();
          }
          return response.blob();
        },
      }),
    }),

    exportDepositPdf: builder.mutation<Blob, IDepositStoresSummaryParams>({
      query: (filters) => ({
        url: `/deposit-reconciliation/export.pdf?${toQueryParams(filters)}`,
        method: "GET",
        responseHandler: async (response: Response) => {
          if (response.headers.get("content-type")?.includes("json")) {
            return response.json();
          }
          return response.blob();
        },
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetDepositStoresSummaryQuery,
  useGetDepositBanksQuery,
  useGetDepositItemsQuery,
  useGetDepositBatchesQuery,
  useListDepositDraftsQuery,
  useGetDepositDraftDetailQuery,
  useCreateDepositDraftMutation,
  useDiscardDepositDraftMutation,
  useCommitAllDepositDraftsMutation,
  useUndoDepositBatchMutation,
  useReprocessDepositsMutation,
  useGetDepositAggregationsQuery,
  useExportDepositExcelMutation,
  useExportDepositPdfMutation,
} = depositReconciliationApi;
